import { TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import axios from 'axios';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import NotAvailableVirusScan from 'src/assets/img/not-available-virus-scan.png';
import NotAvailable from 'src/assets/img/not-available.png';
import { SetState } from 'src/types/generic';
import NumberInput from './NumberInput';

const minScale = 40;
const maxScale = 500;
const step = 20;
const initialScale = 100;

interface ImageActionsProps {
  setRotate: SetState<number>;
  src: string;
  filename?: string;
  zoomIn: () => void;
  zoomOut: () => void;
  scale: number;
  handleChangeScale: (event: TextBoxChangeEvent) => void;
}

const ImageActions: FC<ImageActionsProps> = ({
  setRotate,
  src,
  filename,
  zoomIn,
  zoomOut,
  scale,
  handleChangeScale,
}) => (
  <div className="flex flex-row gap-2 items-center justify-center mb-2 flex-wrap">
    Rotate:
    <button
      className="bg-dark rounded-full h-8 w-8 flex items-center justify-center text-white"
      onClick={() => setRotate((prev) => prev + 90)}
    >
      <span className="k-icon k-i-reload-sm k-i-refresh-sm " />
    </button>
    <button
      className="bg-dark rounded-full h-8 w-8 flex items-center justify-center text-white"
      onClick={() => setRotate((prev) => prev - 90)}
    >
      <span className="k-icon k-i-reset-sm" />
    </button>
    Zoom:
    <button
      className="bg-dark rounded-full h-8 w-8 flex items-center justify-center text-white"
      onClick={zoomOut}
    >
      <span className="k-icon k-i-minus-sm" />
    </button>
    <div className="w-28">
      <NumberInput
        step={step}
        className="text-center"
        min={minScale}
        max={maxScale}
        value={scale.toString()}
        valid
        onChange={handleChangeScale}
        postfix="%"
      />
    </div>
    <button
      className={
        'bg-dark rounded-full h-8 w-8 flex items-center justify-center text-white cursor-pointer '
      }
      onClick={zoomIn}
    >
      <span className="k-icon k-i-plus-sm" />
    </button>
    <a
      href={src}
      download={filename}
      className={
        'bg-dark rounded-full h-8 w-8 flex items-center justify-center text-white cursor-pointer '
      }
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="w-4 h-4"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
        />
      </svg>
    </a>
  </div>
);

const Image = (
  props: React.ImgHTMLAttributes<HTMLImageElement> & {
    styles?: Record<string, string>;
    controls?: boolean;
    filename?: string;
    imageUrl?: string;
    isOcr?: boolean;
  }
) => {
  const [scale, setScale] = useState(initialScale);
  const [loading, setLoading] = useState(true);
  const [src, setSrc] = useState('');
  const [rotate, setRotate] = useState(0);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    if (props?.imageUrl) {
      setSrc(props.imageUrl);
      setLoading(false);
      setError(false);
    } else if (props?.src) {
      axios
        .get(props?.src || '', { responseType: 'blob' })
        .then((response) => {
          setSrc(window.URL.createObjectURL(response.data));
          setError(false);
        })
        .catch(() => {
          setError(true);
        })
        .finally(() => setLoading(false));
    } else {
      setSrc('');
      setError(true);
      setLoading(false);
    }
  }, [props?.filename, props?.imageUrl, props?.src]);

  const img = useMemo(
    () => (
      <img
        {...props}
        src={src}
        alt={props.alt || 'Not Found'}
        style={{
          ...props.styles,
          ...(props.controls && {
            transform: `rotate(${rotate}deg)`,
          }),
        }}
        className="object-contain"
      />
    ),
    [props, rotate, src]
  );

  const handleZoomIn = useCallback(
    (centerView: (scale?: number) => void) => () => {
      setScale((prev) => {
        const newScale = Number((prev + step).toFixed(2));
        if (newScale <= maxScale) {
          centerView(newScale / 100);
          return newScale;
        }
        centerView(maxScale / 100);
        return maxScale;
      });
    },
    []
  );

  const handleZoomOut = useCallback(
    (centerView: (scale?: number) => void) => () => {
      setScale((prev) => {
        const newScale = Number((prev - step).toFixed(2));
        if (newScale >= minScale) {
          centerView(newScale / 100);
          return newScale;
        }
        centerView(minScale / 100);
        return minScale;
      });
    },
    []
  );

  const handleZoomChange = useCallback(
    (centerView: (scale?: number) => void) => (e: TextBoxChangeEvent) => {
      const newScale = Number(e.value || minScale);
      centerView(newScale / 100);
      setScale(newScale);
    },
    []
  );

  return (
    <>
      {loading ? (
        <div className="w-auto h-[50vh] flex items-center justify-center">
          <span className="k-icon k-i-loading text-lg" />
        </div>
      ) : error || !src ? (
        <img
          src={props.isOcr ? NotAvailableVirusScan : NotAvailable}
          alt="Not Available"
          className={`block mx-auto ${props.isOcr ? 'px-24' : ''}`}
        />
      ) : props.controls ? (
        <TransformWrapper
          initialScale={initialScale / 100}
          minScale={minScale / 100}
          maxScale={maxScale / 100}
          centerOnInit
          centerZoomedOut
          disablePadding
          onZoom={(ref) => {
            setScale(Number(ref.state.scale.toFixed(2)) * 100);
          }}
        >
          {({ centerView }) => (
            <>
              <ImageActions
                setRotate={setRotate}
                src={src}
                filename={props?.filename}
                zoomIn={handleZoomIn(centerView)}
                zoomOut={handleZoomOut(centerView)}
                scale={scale}
                handleChangeScale={handleZoomChange(centerView)}
              />
              <div className="flex items-center justify-center overflow-auto">
                <TransformComponent>{img}</TransformComponent>
              </div>
            </>
          )}
        </TransformWrapper>
      ) : (
        img
      )}
    </>
  );
};

export default Image;
